// filepath: apps/web/src/features/lists/tree/TreeCountBanner.tsx
//
// FR-UI-VIEW-2 AC-8 — "X of Y records shown." count banner.
//
// Triggered when the server returns a `total` field that exceeds
// `records.length` AND `records.length` has reached the server-enforced
// cap (TREE_RECORDS_CAP = 500). The banner copy is locked per the
// FR-UI-VIEW-1 AC-3 convention — the user must not see localisation
// drift on the cap message; the wire naming is API-stable
// (ARCHITECTURE_WEB.md §5 detail pages).
//
// INVARIANT REMINDERS:
//   - The banner is off by default — it surfaces only when the
//     server indicates a partial result.
//   - The chrome is a single static sentence; no interpretation
//     of the value (the SPA never formats "12 of 5,123 records
//     shown" with arbitrary locale-aware number formatting on
//     this surface — the count is operational, not visualised).
//   - The banner does NOT use Banner/Card — it is a plain
//     informational notice with role="status" so screen readers
//     receive a polite announcement when it appears, without
//     stealing focus.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { TREE_RECORDS_CAP } from "./hooks/useTreeRecords";

export interface TreeCountBannerProps {
  /** Number of records currently rendered in the tree. */
  shown: number;
  /**
   * Server-supplied total record count (across all roots + orphans +
   * children). Null when the server wire omits the additive `total`
   * field (older deployments) — the banner stays off in that case.
   */
  total: number | null;
  /**
   * Cap above which the banner is considered active. Defaults to
   * `TREE_RECORDS_CAP` (500). Exposed as a prop so a future FR
   * bumping the cap can pass a different threshold.
   */
  cap?: number;
  /** Test hook forwarded onto the rendered root. */
  "data-testid"?: string;
}

/**
 * Predicate — should the banner be visible? Per FR-UI-VIEW-2 AC-8,
 * the banner triggers when the server caps the result set, i.e.
 * `shown >= cap` AND `total > cap`.
 *
 * `total < shown` is impossible under the wire contract (server
 * always supplies total >= page size) but we treat it as `false` to
 * remain defensive against a misbehaving backend (banner stays off
 * when the data is internally inconsistent).
 */
export function shouldShowTreeCountBanner(
  shown: number,
  total: number | null,
  cap: number,
): boolean {
  if (total === null) return false;
  if (!Number.isFinite(total) || total <= 0) return false;
  if (!Number.isFinite(shown) || shown < 0) return false;
  if (total <= cap) return false;
  if (shown < cap) return false;
  return shown < total;
}

/**
 * TreeCountBanner — locked-copy partial-result notice.
 *
 * Renders only when `shouldShowTreeCountBanner(shown, total, cap)`
 * returns true. Otherwise returns null (the parent does not need
 * to track visibility).
 *
 * The displayed copy comes from i18n key `lists.tree.count_banner`
 * with `{{shown}}` and `{{total}}` placeholders. When the key is
 * absent from the active locale, `useTranslation` returns the
 * supplied fallback (`"{{shown}} of {{total}} records shown."`) so
 * the user always sees a parseable string — the invariant is
 * "locale never produces empty chrome".
 */
export function TreeCountBanner({
  shown,
  total,
  cap = TREE_RECORDS_CAP,
  "data-testid": testId = "tree-count-banner",
}: TreeCountBannerProps): React.ReactElement | null {
  if (!shouldShowTreeCountBanner(shown, total, cap)) {
    return null;
  }
  const { t } = useTranslation();
  // LOCKED COPY — see file header. The translation supplies both the
  // localised form AND the English fallback. Both shapes carry the
  // same {{shown}} / {{total}} slots so a locale-specific drift
  // cannot occur at the placeholder level.
  // shouldShowTreeCountBanner returns true only when total is a
  // positive integer > cap, so the cast here is safe.
  const message = t(
    "lists.tree.count_banner",
    "{{shown}} of {{total}} records shown.",
    { shown, total: total as number },
  );
  return (
    <p
      role="status"
      aria-live="polite"
      data-testid={testId}
      data-tree-shown={shown}
      data-tree-total={total}
      data-tree-cap={cap}
      className="tree-count-banner"
      style={{
        margin: "0 0 var(--space-3) 0",
        padding: "var(--space-2) var(--space-4)",
        background: "var(--surface-sunken)",
        color: "var(--text-body)",
        fontFamily: "var(--font-sans)",
        fontSize: "var(--text-sm)",
        border: "1px solid var(--border-default)",
        borderRadius: "var(--radius-sm)",
      }}
    >
      {message}
    </p>
  );
}
